import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Phone, Search, Home, Bookmark, AlertCircle, Filter as FilterIcon, Trash2, Square, CheckSquare, ChevronDown, ChevronRight } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatEuro, contractProgress, daysSince } from "@/lib/format";
import QuickCallDialog from "@/components/QuickCallDialog";
import LiveStatusBadge from "@/components/LiveStatusBadge";
import StatusChip from "@/components/StatusChip";
import { formatTimeSince } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadBulkBar, { leadDeleteMessage } from "@/components/LeadBulkBar";
import BulkArchiveDialog from "@/components/BulkArchiveDialog";
import LeadFilterChips, { readFilterFromParams, writeFilterToParams, filterToApiParams } from "@/components/LeadFilterChips";
import { useLeadSelection } from "@/lib/useLeadSelection";

const FILTERS = [
  { k: "all",              label: "Alle leads" },
  { k: "ready",            label: "Klaar voor prospectie (≥90d)" },
  { k: "pending",          label: "In afwachting (<90d)" },
  { k: "price_drop",       label: "Met prijsdaling" },
  { k: "possibly_offline", label: "Mogelijk offline" },
];

/**
 * Parkeren — original purpose: makelaar-leads in prospection mode.
 * Shows ALL makelaar leads regardless of their status, with a filter
 * dropdown to focus on ≥90d / price drops / etc.
 *
 * Status-specific tabs (Zoeken/Shortlist/Getekend/Verloren) live in
 * separate top-level pages — this one is the prospection cockpit.
 */
export default function Parkeren() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filter = params.get("filter") || "all";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLead, setCallLead] = useState(null);
  const [query, setQuery] = useState("");
  const chipFilter = readFilterFromParams(params);
  const setChipFilter = (next) => setParams(writeFilterToParams(next, params));

  // Request-token tegen race conditions tussen achtereenvolgende fetches.
  const reqIdRef = useRef(0);

  const refresh = async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    const p = { source_type: "makelaar", brief_step: "none", ...filterToApiParams(chipFilter) };
    if (filter !== "all") p.prospection = filter;
    try {
      const r = await http.get("/api/leads", { params: p });
      if (myId !== reqIdRef.current) return;
      setLeads(r.data);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  };
  useEffect(() => { setLeads([]); refresh(); /* eslint-disable-next-line */ },
    [filter, params.get("f_status"), params.get("f_live"), params.get("f_option"), params.get("f_pmin"), params.get("f_pmax"), params.get("f_action")]);

  const filtered = useMemo(() => {
    let arr = leads;
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter(l =>
        l.address?.toLowerCase().includes(q) ||
        l.postal_code?.includes(q) ||
        l.city?.toLowerCase().includes(q) ||
        l.agent_name?.toLowerCase().includes(q)
      );
    }
    // Sort: oldest first (most "ready for prospection") so panden ≥3 mnd
    // bubble to the top of the list. Within ties, agent name.
    return [...arr].sort((a, b) => {
      const ad = daysSince(a.date_posted) ?? 0;
      const bd = daysSince(b.date_posted) ?? 0;
      return bd - ad;
    });
  }, [leads, query]);

  const setFilter = (k) => {
    const n = new URLSearchParams(params);
    if (k === "all") n.delete("filter"); else n.set("filter", k);
    setParams(n);
  };

  const openLead = (id) => {
    navigate(`/lead/${id}`, {
      state: { from: location.pathname + location.search, list: filtered.map(l => l.id) },
    });
  };

  const readyCount = useMemo(
    () => leads.filter(l => (daysSince(l.date_posted) || 0) >= 90).length,
    [leads],
  );

  const sel = useLeadSelection(refresh);
  // Bucket-collapse state, persisted across mounts via localStorage
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("parkeren.collapsed") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("parkeren.collapsed", JSON.stringify(collapsed)); }
    catch (e) { console.debug("localStorage write failed (parkeren.collapsed)", e); }
  }, [collapsed]);

  return (
    <div className="flex-1 min-w-0 p-8 overflow-auto" data-testid="parkeren-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            Pipeline
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            Parkeren
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1 max-w-2xl">
            Makelaar-leads in prospectie. Leads ouder dan 90 dagen krijgen een groene markering — klaar voor benadering.
          </p>
        </div>
        {filter === "all" && readyCount > 0 && (
          <div className="chip chip-teal !text-[12px]" data-testid="parkeren-ready-count">
            {readyCount} × ≥90d
          </div>
        )}
      </div>

      {/* Filter dropdown + search */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FilterIcon size={14} className="text-[var(--ink-400)]" />
          <span className="text-[12px] text-[var(--ink-500)] uppercase tracking-wider">Filter</span>
          <select
            data-testid="parkeren-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-10 px-3 pr-8 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)] min-w-[260px]"
          >
            {FILTERS.map(f => (
              <option key={f.k} value={f.k}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
          <input
            data-testid="parkeren-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`${t("common.search")}…`}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm focus:border-[var(--teal-500)] outline-none"
          />
        </div>
      </div>
      <div className="mb-6">
        <LeadFilterChips value={chipFilter} onChange={setChipFilter} sourceType="makelaar"
                         actionFilters={[{ key: "succes_brief", label: "Succes brief" }]} />
      </div>

      {loading ? (
        <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 px-6" data-testid="parkeren-empty">
          <h3 className="font-display text-[20px]">Geen leads in dit filter</h3>
          <p className="text-[14px] text-[var(--ink-500)] mt-2 max-w-md mx-auto">
            Pas het filter aan of wacht tot nieuwe Makelaar-leads via Flowzy binnenkomen.
          </p>
        </div>
      ) : (
        (() => {
          // Bucket sort: ≥3 mnd "klaar voor prospectie" first, then 1–3 mnd, then nieuw (<1 mnd)
          const ready = filtered.filter(l => (daysSince(l.date_posted) || 0) >= 90);
          const ripening = filtered.filter(l => {
            const d = daysSince(l.date_posted) || 0;
            return d >= 30 && d < 90;
          });
          const fresh = filtered.filter(l => (daysSince(l.date_posted) || 0) < 30);
          const Section = ({ title, subtitle, leads, accent, bucketKey, defaultOpen = true }) => {
            const open = collapsed[bucketKey] === undefined ? defaultOpen : !collapsed[bucketKey];
            const toggle = () => setCollapsed(c => ({ ...c, [bucketKey]: open }));
            if (leads.length === 0) return null;
            return (
              <div className="mb-6" data-testid={`parkeren-bucket-${accent}`}>
                <button type="button" onClick={toggle}
                        data-testid={`parkeren-bucket-toggle-${accent}`}
                        className="w-full flex items-baseline justify-between mb-2 text-left hover:opacity-80 transition-opacity">
                  <div className="flex items-center gap-2">
                    {open
                      ? <ChevronDown size={14} className="text-[var(--ink-400)]" />
                      : <ChevronRight size={14} className="text-[var(--ink-400)]" />}
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      accent === "ready" ? "bg-[var(--teal-600)]" :
                      accent === "ripening" ? "bg-[#E5C9A0]" : "bg-[var(--ink-300)]"
                    }`} />
                    <h3 className={`font-display text-[18px] ${
                      accent === "ready" ? "text-[var(--teal-800)]" : "text-[var(--ink-900)]"
                    }`}>{title}</h3>
                    <span className="text-[12px] text-[var(--ink-500)]">({leads.length})</span>
                  </div>
                  <span className="text-[11px] text-[var(--ink-400)] uppercase tracking-wider">{subtitle}</span>
                </button>
                {open && (
                  <ul className="space-y-3">
                    {leads.map(lead => (
                      <ParkeerRow key={lead.id} lead={lead}
                                  selected={sel.selected.has(lead.id)}
                                  onToggle={() => sel.toggleOne(lead.id)}
                                  onSelect={() => openLead(lead.id)}
                                  onCall={() => setCallLead(lead)}
                                  onDelete={() => sel.requestDelete([lead.id])} />
                    ))}
                  </ul>
                )}
              </div>
            );
          };
          return (
            <div data-testid="parkeren-list">
              <Section title="Klaar voor prospectie" subtitle="≥ 3 maanden online"
                       leads={ready} accent="ready" bucketKey="ready" />
              <Section title="In rijping" subtitle="1 – 3 maanden online"
                       leads={ripening} accent="ripening" bucketKey="ripening" />
              <Section title="Nieuw" subtitle="< 1 maand online"
                       leads={fresh} accent="fresh" bucketKey="fresh" defaultOpen={false} />
            </div>
          );
        })()
      )}

      <LeadBulkBar
        count={sel.selected.size}
        total={filtered.length}
        allSelected={filtered.length > 0 && filtered.every(l => sel.selected.has(l.id))}
        onToggleAll={() => sel.toggleAll(filtered.map(l => l.id))}
        onClear={sel.clear}
        onArchive={() => sel.requestArchive(Array.from(sel.selected))}
        onDelete={() => sel.requestDelete(Array.from(sel.selected))}
      />

      <BulkArchiveDialog sel={sel} />

      <ConfirmDialog
        open={sel.confirmIds !== null}
        danger
        title={(sel.confirmIds?.length || 0) > 1 ? "Leads verwijderen" : "Lead verwijderen"}
        message={leadDeleteMessage(sel.confirmIds)}
        confirmLabel="Verwijderen"
        onConfirm={sel.doDelete}
        onCancel={sel.cancelDelete}
      />

      {callLead && (
        <QuickCallDialog lead={callLead} onClose={() => setCallLead(null)}
                         onSaved={() => { setCallLead(null); refresh(); }} />
      )}
    </div>
  );
}

function ParkeerRow({ lead, onSelect, onCall, onDelete, selected, onToggle }) {
  const prog = contractProgress(lead.date_posted, lead.source_type);
  const days = daysSince(lead.date_posted);
  const isReady = (days || 0) >= 90;
  const hasPriceDrop = lead.old_price > 0 && lead.price > 0 && lead.old_price > lead.price;
  const thumb = lead.photos?.[0];
  return (
    <li onClick={onSelect}
        data-testid={`lead-row-${lead.id}`}
        className={`card cursor-pointer !p-4 grid grid-cols-12 gap-4 items-center transition-all
          ${selected
            ? "!border-[var(--teal-500)] !bg-[var(--sand-50)]"
            : isReady
              ? "!bg-[var(--teal-50)] !border-[var(--teal-500)] hover:!bg-[#D8EFEC]"
              : "hover:border-[var(--border-strong)]"}`}>
      <div className="col-span-1 shrink-0 flex items-center gap-2">
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
                data-testid={`lead-select-${lead.id}`}
                className="grid place-items-center w-6 h-6 text-[var(--ink-400)] hover:text-[var(--teal-700)]"
                title={selected ? "Deselecteren" : "Selecteren"}>
          {selected ? <CheckSquare size={18} className="text-[var(--teal-700)]" /> : <Square size={18} />}
        </button>
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--sand-200)] grid place-items-center">
          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                 : <Home size={16} className="text-[var(--ink-400)]" />}
        </div>
      </div>
      <div className="col-span-4 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <StatusChip lead={lead} />
          {isReady && (
            <span data-testid={`lead-ready-${lead.id}`}
                  className="chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                             bg-[var(--teal-700)] text-white border-transparent flex items-center gap-1">
              <AlertCircle size={11} /> ≥90d
            </span>
          )}
          {hasPriceDrop && (
            <span className="chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                             bg-[var(--warn,#f59e0b)] text-white border-transparent">
              prijsdaling
            </span>
          )}
          <LiveStatusBadge lead={lead} />
        </div>
        <div className="text-[15px] font-semibold text-[var(--ink-900)] truncate">
          {lead.address} · {lead.postal_code} {lead.city}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] mt-1 flex flex-wrap gap-x-3">
          {lead.agent_name && <span>{lead.agent_name}</span>}
          <span>Online {formatTimeSince(lead.date_posted)}</span>
        </div>
      </div>
      <div className="col-span-2 tabular">
        <div className="text-[15px] font-semibold">{formatEuro(lead.price)}</div>
        {hasPriceDrop && (
          <div className="text-[12px] text-[var(--ink-400)] line-through">{formatEuro(lead.old_price)}</div>
        )}
      </div>
      <div className="col-span-3">
        <div className={`text-[13px] tabular font-semibold ${isReady ? "text-[var(--teal-700)]" : "text-[var(--ink-700)]"}`}>
          {formatTimeSince(lead.date_posted)} online
          {isReady && <span className="ml-2 text-[11px] font-normal text-[var(--ink-500)]">
            · klaar voor prospectie
          </span>}
        </div>
        <div className="timebar mt-1.5">
          <div className={`timebar-fill ${prog.tone === "warn" ? "warn" : prog.tone === "danger" ? "danger" : ""}`}
               style={{ width: `${prog.pct}%` }} />
        </div>
      </div>
      <div className="col-span-2 flex justify-end items-center gap-2">
        {lead.shortlisted && <Bookmark size={16} className="text-[var(--teal-600)] fill-[var(--teal-500)]" />}
        <button type="button" onClick={(e) => { e.stopPropagation(); onCall(); }}
                data-testid={`btn-call-${lead.id}`}
                className="btn btn-ghost h-9 !px-3">
          <Phone size={14} /> {t("leads.call")}
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
                data-testid={`btn-delete-${lead.id}`}
                title="Lead verwijderen"
                className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]">
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}
